// Battle background music. Picks a random song, plays to completion,
// then picks a different one (never the immediately previous).
// Volume (0-100) and muted are stored independently so dragging to 0
// is distinct from muting — un-mute restores the previous slider.

const VOL_KEY = "musicVolume";
const MUTE_KEY = "musicMuted";
const SONG_URLS: readonly string[] = [
  "/audio/ai_battle_music_1.mp3",
  "/audio/ai_battle_music_2.mp3",
  "/audio/ai_battle_music_3.mp3",
  "/audio/ai_battle_music_4.mp3",
  "/audio/ai_battle_music_5.mp3",
  "/audio/ai_battle_music_6.mp3",
  "/audio/ai_battle_music_7.mp3",
  "/audio/ai_battle_music_8.mp3",
];
const DEFAULT_MUSIC_VOLUME = 50;

let _volume = readVolume();
let _muted = readMuted();
let _audio: HTMLAudioElement | null = null;
let _lastIndex = -1;
let _running = false;
let _rng: () => number = Math.random;
const _listeners = new Set<() => void>();

function readVolume(): number {
  try {
    const raw = window.localStorage.getItem(VOL_KEY);
    if (raw == null) return DEFAULT_MUSIC_VOLUME;
    const n = Number(raw);
    if (!Number.isFinite(n)) return DEFAULT_MUSIC_VOLUME;
    return clamp(n);
  } catch {
    return DEFAULT_MUSIC_VOLUME;
  }
}

function readMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeVolume(v: number) {
  try {
    window.localStorage.setItem(VOL_KEY, String(v));
  } catch {
    // ignore — playback still works without persistence
  }
}

function writeMuted(m: boolean) {
  try {
    window.localStorage.setItem(MUTE_KEY, m ? "1" : "0");
  } catch {
    // ignore — playback still works without persistence
  }
}

function clamp(v: number): number {
  if (v < 0) return 0;
  if (v > 100) return 100;
  return Math.round(v);
}

function notify() {
  for (const cb of _listeners) cb();
}

function effectiveGain(): number {
  return _muted ? 0 : _volume / 100;
}

export function getMusicVolume(): number {
  return _volume;
}

export function setMusicVolume(v: number): void {
  const next = clamp(v);
  if (next === _volume) return;
  _volume = next;
  writeVolume(next);
  applyGain();
  notify();
}

export function isMusicMuted(): boolean {
  return _muted;
}

export function setMusicMuted(m: boolean): void {
  if (m === _muted) return;
  _muted = m;
  writeMuted(m);
  applyGain();
  notify();
}

export function subscribeMusic(cb: () => void): () => void {
  _listeners.add(cb);
  return () => {
    _listeners.delete(cb);
  };
}

function applyGain() {
  if (_audio) _audio.volume = effectiveGain();
}

function pickNextIndex(): number {
  if (SONG_URLS.length <= 1) return 0;
  let i: number;
  do {
    i = Math.floor(_rng() * SONG_URLS.length);
  } while (i === _lastIndex);
  return i;
}

function playNext() {
  if (!_running) return;
  const idx = pickNextIndex();
  _lastIndex = idx;

  // Tear down the previous element so its `ended` can't double-fire.
  if (_audio) {
    _audio.onended = null;
    try {
      _audio.pause();
    } catch {
      // ignore
    }
  }

  const a = new Audio(SONG_URLS[idx]);
  a.volume = effectiveGain();
  a.onended = () => {
    // Late-cancel guard: stop() may have run between `ended` and here.
    if (!_running || _audio !== a) return;
    playNext();
  };
  _audio = a;
  // Autoplay policy can reject; the click that opened BattlePage
  // counts as a gesture so it usually succeeds.
  void a.play().catch(() => {});
}

/** Idempotent. Returns a (also-idempotent) stop. */
export function start(): () => void {
  if (_running) return stop;
  _running = true;
  playNext();
  return stop;
}

export function stop(): void {
  _running = false;
  const a = _audio;
  _audio = null;
  if (!a) return;
  a.onended = null;
  try {
    a.pause();
  } catch {
    // already paused / never started
  }
}

/** Test-only. */
export function __resetForTests(): void {
  stop();
  _lastIndex = -1;
  _volume = DEFAULT_MUSIC_VOLUME;
  _muted = false;
  _rng = Math.random;
  _listeners.clear();
}

/** Test-only: deterministic RNG for the no-repeat rule. */
export function __setRngForTests(fn: () => number): void {
  _rng = fn;
}

/** Test-only. */
export function __songCountForTests(): number {
  return SONG_URLS.length;
}
